import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Search } from 'lucide-react'
import { fetchAllAdminUsers } from '../../api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { formatAppDateTime } from '@/lib/locale'
import { cn } from '@/lib/utils'

const ROLE_FILTERS = [
  { key: '', labelKey: 'admin.allUsers.filterAll' },
  { key: 'super-admin', labelKey: 'admin.allUsers.filterSuperAdmin' },
  { key: 'business-owner', labelKey: 'admin.allUsers.filterBusinessOwner' },
  { key: 'agent', labelKey: 'admin.allUsers.filterAgent' },
] as const

const ROLE_BADGE_COLORS: Record<string, string> = {
  'super-admin':
    'border-violet-300 bg-violet-50 text-violet-700 dark:border-violet-800 dark:bg-violet-950/40 dark:text-violet-300',
  'business-owner':
    'border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-300',
  agent:
    'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300',
}

function userInitials(name: string) {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('')
}

function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation()
  const colorMap: Record<string, string> = {
    active: 'border-green-300 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950/40 dark:text-green-400',
    suspended: 'border-red-300 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-400',
    invited: 'border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-400',
  }
  return (
    <Badge variant="outline" className={cn(colorMap[status] ?? '')}>
      {t(`admin.users.status.${status}` as const, { defaultValue: status })}
    </Badge>
  )
}

export function AllUsersTab() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [roleFilter, setRoleFilter] = useState('')
  const [search, setSearch] = useState('')

  const usersQuery = useQuery({
    queryKey: ['admin', 'all-users', roleFilter, search],
    queryFn: () => fetchAllAdminUsers({ role: roleFilter || undefined, search: search || undefined }),
  })

  const users = usersQuery.data?.data ?? []

  return (
    <>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-1.5">
          {ROLE_FILTERS.map((f) => (
            <Button
              key={f.key}
              size="sm"
              variant={roleFilter === f.key ? 'default' : 'outline'}
              onClick={() => setRoleFilter(f.key)}
              className="h-7 text-xs"
            >
              {t(f.labelKey)}
            </Button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('admin.allUsers.searchPlaceholder')}
              className="h-8 pl-8 sm:w-56"
            />
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-border">
        <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('admin.allUsers.table.user')}</TableHead>
              <TableHead>{t('admin.allUsers.table.role')}</TableHead>
              <TableHead>{t('admin.allUsers.table.status')}</TableHead>
              <TableHead>{t('admin.allUsers.table.lastLogin')}</TableHead>
              <TableHead>{t('admin.allUsers.table.createdAt')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {usersQuery.isPending
              ? Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Skeleton className="size-8 rounded-full" />
                        <div className="space-y-1">
                          <Skeleton className="h-4 w-28" />
                          <Skeleton className="h-3 w-40" />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell><Skeleton className="h-5 w-20" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-14" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                  </TableRow>
                ))
              : users.length === 0
                ? (
                  <TableRow>
                    <TableCell colSpan={5} className="py-12 text-center text-sm text-muted-foreground">
                      {t('admin.allUsers.emptyState')}
                    </TableCell>
                  </TableRow>
                )
                : users.map((user) => (
                  <TableRow
                    key={user.id}
                    className="cursor-pointer hover:bg-muted/40"
                    onClick={() => navigate(`/admin/users/${user.id}`)}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="size-8">
                          <AvatarImage src={user.avatar_url ?? undefined} alt={user.display_name} />
                          <AvatarFallback className="text-xs font-semibold">
                            {userInitials(user.display_name)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="text-sm font-medium">{user.display_name}</p>
                          <p className="text-xs text-muted-foreground">{user.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {user.roles.length === 0 ? (
                          <span className="text-xs text-muted-foreground">—</span>
                        ) : (
                          user.roles.map((r) => (
                            <Badge
                              key={r.id}
                              variant="outline"
                              className={cn('text-xs', ROLE_BADGE_COLORS[r.slug] ?? '')}
                            >
                              {r.name}
                            </Badge>
                          ))
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={user.status} />
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {user.last_login_at
                        ? formatAppDateTime(user.last_login_at)
                        : '—'}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatAppDateTime(user.created_at)}
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
        </div>
      </div>

    </>
  )
}
